import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  APRCalculationParams,
  APRCalculationResult,
  calculateAPRWithLoanType,
} from '@/utils/aprUtils';
import {
  createRateMatrix,
  getDefaultRateMatrices,
  LoanType,
  RateMatrix,
  updateRateCell,
  validateRateMatrix,
} from '@/utils/rateMatrixUtils';

// State interface
export interface InterestRateState {
  rateMatrices: Record<LoanType, RateMatrix>;
  matricesLoaded: boolean;
  // Admin-controlled APR assumptions per loan type (loaded from backend)
  aprAssumptions: Partial<Record<LoanType, Record<string, any>>>;
  // Admin-controlled closing cost fee items per loan type (loaded from backend)
  feeItemsByLoanType: Partial<Record<LoanType, any[]>>;
  calculatedFeeRulesByLoanType: Partial<Record<LoanType, any[]>>;
  currentAPRCalculation: APRCalculationParams | null;
  aprResult: APRCalculationResult | null;
  loading: boolean;
  error: string | null;
  selectedLoanType: LoanType | null;
  rateChangeHistory: RateChangeHistory[];
  draftChanges: DraftChange[];
  activeConfigurationId: string | null;
}

export interface RateChangeHistory {
  id: string;
  loanType: LoanType;
  timestamp: string;
  reason: string;
  previousRates: number[][];
  newRates: number[][];
  changedBy: string;
}

export interface DraftChange {
  id: string;
  loanType: LoanType;
  changeType: 'matrix-cell' | 'reset';
  payload: Record<string, unknown>;
  reason: string;
  changedBy: string;
  timestamp: string;
}

// Initial state
const initialState: InterestRateState = {
  rateMatrices: getDefaultRateMatrices(),
  matricesLoaded: false,
  aprAssumptions: {},
  feeItemsByLoanType: {},
  calculatedFeeRulesByLoanType: {},
  currentAPRCalculation: null,
  aprResult: null,
  loading: false,
  error: null,
  selectedLoanType: null,
  rateChangeHistory: [],
  draftChanges: [],
  activeConfigurationId: null,
};

// Async thunks
export const updateRateMatrixAsync = createAsyncThunk(
  'interestRate/updateMatrix',
  async (
    {
      loanType,
      rowIndex,
      colIndex,
      newRate,
      reason,
      changedBy,
    }: {
      loanType: LoanType;
      rowIndex: number;
      colIndex: number;
      newRate: number;
      reason: string;
      changedBy: string;
    },
    { getState }
  ) => {
    const state = getState() as { interestRate: InterestRateState };
    const currentMatrix = state.interestRate.rateMatrices[loanType];

    // Store previous rates for history
    const previousRates = currentMatrix.matrix.map((row) =>
      row.map((cell) => cell.rate)
    );

    // Update the matrix
    const updatedMatrix = updateRateCell(
      currentMatrix,
      rowIndex,
      colIndex,
      newRate
    );

    // Validate the updated matrix
    const validation = validateRateMatrix(updatedMatrix);
    if (!validation.isValid) {
      throw new Error(`Invalid rate matrix: ${validation.errors.join(', ')}`);
    }

    const timestamp = new Date().toISOString();

    // Create history entry
    const historyEntry: RateChangeHistory = {
      id: `change_${Date.now()}`,
      loanType,
      timestamp,
      reason,
      previousRates,
      newRates: updatedMatrix.matrix.map((row) => row.map((cell) => cell.rate)),
      changedBy,
    };

    const draftChange: DraftChange = {
      id: `draft_${Date.now()}`,
      loanType,
      changeType: 'matrix-cell',
      payload: { rowIndex, colIndex, newRate },
      reason,
      changedBy,
      timestamp,
    };

    return {
      loanType,
      updatedMatrix,
      historyEntry,
      draftChange,
    };
  }
);

export const resetRateMatrixAsync = createAsyncThunk(
  'interestRate/resetMatrix',
  async (
    {
      loanType,
      reason,
      changedBy,
    }: {
      loanType: LoanType;
      reason: string;
      changedBy: string;
    },
    { getState }
  ) => {
    const state = getState() as { interestRate: InterestRateState };
    const currentMatrix = state.interestRate.rateMatrices[loanType];

    // Store previous rates for history
    const previousRates = currentMatrix.matrix.map((row) =>
      row.map((cell) => cell.rate)
    );

    // Create new default matrix
    const newMatrix = createRateMatrix(loanType, reason);

    const timestamp = new Date().toISOString();

    // Create history entry
    const historyEntry: RateChangeHistory = {
      id: `reset_${Date.now()}`,
      loanType,
      timestamp,
      reason,
      previousRates,
      newRates: newMatrix.matrix.map((row) => row.map((cell) => cell.rate)),
      changedBy,
    };

    const draftChange: DraftChange = {
      id: `draft_${Date.now()}`,
      loanType,
      changeType: 'reset',
      payload: { newMatrix: newMatrix.matrix },
      reason,
      changedBy,
      timestamp,
    };

    return {
      loanType,
      updatedMatrix: newMatrix,
      historyEntry,
      draftChange,
    };
  }
);

export const calculateAPRAsync = createAsyncThunk(
  'interestRate/calculateAPR',
  async (params: APRCalculationParams) => {
    const result = calculateAPRWithLoanType(params);
    return result;
  }
);

// Slice
const interestRateSlice = createSlice({
  name: 'interestRate',
  initialState,
  reducers: {
    setSelectedLoanType: (state, action: PayloadAction<LoanType | null>) => {
      state.selectedLoanType = action.payload;
    },

    setCurrentAPRCalculation: (
      state,
      action: PayloadAction<APRCalculationParams | null>
    ) => {
      state.currentAPRCalculation = action.payload;
      state.aprResult = null; // Clear previous result
    },

    clearError: (state) => {
      state.error = null;
    },

    addRateChangeHistory: (state, action: PayloadAction<RateChangeHistory>) => {
      state.rateChangeHistory.unshift(action.payload);
      // Keep only last 100 entries
      if (state.rateChangeHistory.length > 100) {
        state.rateChangeHistory = state.rateChangeHistory.slice(0, 100);
      }
    },

    clearRateChangeHistory: (state) => {
      state.rateChangeHistory = [];
    },

    // Bulk update rates for a matrix
    bulkUpdateRates: (
      state,
      action: PayloadAction<{
        loanType: LoanType;
        newRates: number[][];
        reason: string;
        changedBy: string;
      }>
    ) => {
      const { loanType, newRates, reason, changedBy } = action.payload;
      const currentMatrix = state.rateMatrices[loanType];

      // Store previous rates for history
      const previousRates = currentMatrix.matrix.map((row) =>
        row.map((cell) => cell.rate)
      );

      // Update matrix with new rates
      const updatedMatrix = {
        ...currentMatrix,
        matrix: currentMatrix.matrix.map((row, i) =>
          row.map((cell, j) => ({
            ...cell,
            rate: newRates[i]?.[j] ?? cell.rate,
          }))
        ),
        lastUpdated: new Date().toISOString(),
        reason,
      };

      state.rateMatrices[loanType] = updatedMatrix;

      // Add to history
      const historyEntry: RateChangeHistory = {
        id: `bulk_${Date.now()}`,
        loanType,
        timestamp: new Date().toISOString(),
        reason,
        previousRates,
        newRates,
        changedBy,
      };

      state.rateChangeHistory.unshift(historyEntry);
      if (state.rateChangeHistory.length > 100) {
        state.rateChangeHistory = state.rateChangeHistory.slice(0, 100);
      }
    },

    setActiveConfigurationId: (state, action: PayloadAction<string | null>) => {
      state.activeConfigurationId = action.payload;
    },

    clearDraftChanges: (state) => {
      state.draftChanges = [];
    },

    removeDraftChange: (state, action: PayloadAction<string>) => {
      state.draftChanges = state.draftChanges.filter(
        (change) => change.id !== action.payload
      );
    },

    // Load rate matrices from backend
    loadRateMatricesFromBackend: (
      state,
      action: PayloadAction<Partial<Record<LoanType, RateMatrix>>>
    ) => {
      // Merge backend matrices with existing state
      // Backend matrices take precedence, but preserve any local draft changes
      Object.entries(action.payload).forEach(([loanType, matrix]) => {
        if (matrix) {
          state.rateMatrices[loanType as LoanType] = matrix;
        }
      });
      state.matricesLoaded = true;
    },

    // Load APR assumptions from backend
    loadAprAssumptionsFromBackend: (
      state,
      action: PayloadAction<Partial<Record<LoanType, Record<string, any>>>>
    ) => {
      Object.entries(action.payload).forEach(([loanType, assumptions]) => {
        if (assumptions) {
          state.aprAssumptions[loanType as LoanType] = assumptions;
        }
      });
    },

    loadFeeItemsFromBackend: (
      state,
      action: PayloadAction<Partial<Record<LoanType, any[]>>>
    ) => {
      Object.entries(action.payload).forEach(([loanType, feeItems]) => {
        if (feeItems) {
          state.feeItemsByLoanType[loanType as LoanType] = feeItems;
        }
      });
    },

    loadCalculatedFeeRulesFromBackend: (
      state,
      action: PayloadAction<Partial<Record<LoanType, any[]>>>
    ) => {
      Object.entries(action.payload).forEach(([loanType, rules]) => {
        if (rules) {
          state.calculatedFeeRulesByLoanType[loanType as LoanType] = rules;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Update rate matrix
      .addCase(updateRateMatrixAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRateMatrixAsync.fulfilled, (state, action) => {
        const { loanType, updatedMatrix, historyEntry, draftChange } =
          action.payload;
        state.rateMatrices[loanType] = updatedMatrix;
        state.rateChangeHistory.unshift(historyEntry);
        if (state.rateChangeHistory.length > 100) {
          state.rateChangeHistory = state.rateChangeHistory.slice(0, 100);
        }
        state.draftChanges.push(draftChange);
        state.loading = false;
      })
      .addCase(updateRateMatrixAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update rate matrix';
      })

      // Reset rate matrix
      .addCase(resetRateMatrixAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetRateMatrixAsync.fulfilled, (state, action) => {
        const { loanType, updatedMatrix, historyEntry, draftChange } =
          action.payload;
        state.rateMatrices[loanType] = updatedMatrix;
        state.rateChangeHistory.unshift(historyEntry);
        if (state.rateChangeHistory.length > 100) {
          state.rateChangeHistory = state.rateChangeHistory.slice(0, 100);
        }
        state.draftChanges.push(draftChange);
        state.loading = false;
      })
      .addCase(resetRateMatrixAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to reset rate matrix';
      })

      // Calculate APR
      .addCase(calculateAPRAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(calculateAPRAsync.fulfilled, (state, action) => {
        state.aprResult = action.payload;
        state.loading = false;
      })
      .addCase(calculateAPRAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to calculate APR';
      });
  },
});

// Export actions
export const {
  setSelectedLoanType,
  setCurrentAPRCalculation,
  clearError,
  addRateChangeHistory,
  clearRateChangeHistory,
  bulkUpdateRates,
  setActiveConfigurationId,
  clearDraftChanges,
  removeDraftChange,
  loadRateMatricesFromBackend,
  loadAprAssumptionsFromBackend,
  loadFeeItemsFromBackend,
  loadCalculatedFeeRulesFromBackend,
} = interestRateSlice.actions;

// Export selectors
export const selectRateMatrices = (state: {
  interestRate: InterestRateState;
}) => state.interestRate.rateMatrices;

export const selectMatricesLoaded = (state: {
  interestRate: InterestRateState;
}) => state.interestRate.matricesLoaded;

export const selectRateMatrixByType =
  (loanType: LoanType) => (state: { interestRate: InterestRateState }) =>
    state.interestRate.rateMatrices[loanType];

export const selectSelectedLoanType = (state: {
  interestRate: InterestRateState;
}) => state.interestRate.selectedLoanType;

export const selectCurrentAPRCalculation = (state: {
  interestRate: InterestRateState;
}) => state.interestRate.currentAPRCalculation;

export const selectAPRResult = (state: { interestRate: InterestRateState }) =>
  state.interestRate.aprResult;

export const selectLoading = (state: { interestRate: InterestRateState }) =>
  state.interestRate.loading;

export const selectError = (state: { interestRate: InterestRateState }) =>
  state.interestRate.error;

export const selectRateChangeHistory = (state: {
  interestRate: InterestRateState;
}) => state.interestRate.rateChangeHistory;

export const selectRateChangeHistoryByType =
  (loanType: LoanType) => (state: { interestRate: InterestRateState }) =>
    state.interestRate.rateChangeHistory.filter(
      (entry) => entry.loanType === loanType
    );

export const selectDraftChanges = (state: {
  interestRate: InterestRateState;
}) => state.interestRate.draftChanges;

export const selectActiveConfigurationId = (state: {
  interestRate: InterestRateState;
}) => state.interestRate.activeConfigurationId;

// Export reducer
export default interestRateSlice.reducer;
